import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Center, Flex, forwardRef, HStack, IconButton, Image, rem, StyleProps, TrashOutline } from '@bonusly/design-system';

import { Conceal } from '../../display/conceal';
import { Reveal } from '../../display/reveal';
import { UploadButton } from '../upload-button';

type ImageUploadInputProps = StyleProps & {
	children?: ReactNode;
	fallback?: ReactNode;
	isDisabled?: boolean;
	isInvalid?: boolean;
	onChange: (value: File | null) => void;
	size?: number | string;
	value?: string | null;
};

export const ImageUploadInput = forwardRef<ImageUploadInputProps, 'button'>(
	({ children, fallback, isDisabled, isInvalid, onChange, size, value, ...rest }, ref) => {
		const { t } = useTranslation('components', { keyPrefix: 'imageUpload' });

		return (
			<HStack spacing={5}>
				<Center
					boxSize={size || rem(250)}
					borderRadius="lg"
					border="1px solid"
					borderColor={isInvalid ? 'error.inputBorder' : 'border.input'}
					bgColor="bg.surface"
					overflow="hidden"
					{...rest}
				>
					{value ? <Image src={value} w="full" h="full" objectFit="cover" /> : fallback}
				</Center>

				<Flex flexDir="column" alignSelf="stretch" alignItems="flex-start" justify="center">
					<Reveal if={isDisabled}>
						<Button mb={2} isDisabled ref={ref}>
							{t('uploadLabel')}
						</Button>
					</Reveal>

					<Conceal if={isDisabled}>
						<HStack mb={2}>
							<UploadButton accept={['jpg', 'png']} onChange={onChange} ref={ref}>
								{t('uploadLabel')}
							</UploadButton>

							{value && (
								<IconButton aria-label={t('removeLabel')} variant="solid" colorScheme="gray" size="sm" isRound onClick={() => onChange(null)}>
									<TrashOutline size="sm" />
								</IconButton>
							)}
						</HStack>
					</Conceal>

					{children}
				</Flex>
			</HStack>
		);
	}
);
